/* Progressive presentation controls. The existing configurator owns design state. */
(() => {
  'use strict';
  document.documentElement.classList.add('premium-ready');

  // Run before theme.js's submit listener synchronises the hidden locale field.
  document.addEventListener('submit', event => {
    const form = event.target;
    const button = event.submitter;
    if (!form.matches('.language-switcher') || button?.name !== 'locale_code') return;
    const select = form.querySelector('[data-locale-select]');
    if (select) select.value = button.value;
  }, true);

  const states = new Map();
  function init(root) {
    if (states.has(root)) return;
    const cleanups = [];
    const on = (node, type, handler) => {
      node.addEventListener(type, handler);
      cleanups.push(() => node.removeEventListener(type, handler));
    };
    const quantity = root.querySelector('input[name="quantity"]');
    if (quantity) {
      const minus = root.querySelector('[data-quantity-minus]');
      const plus = root.querySelector('[data-quantity-plus]');
      if (minus && plus) {
        minus.hidden = plus.hidden = false;
        const sync = () => {
          const min = Number(quantity.min) || 1;
          minus.disabled = Number(quantity.value) <= min;
          plus.disabled = quantity.max !== '' && Number(quantity.value) >= Number(quantity.max);
        };
        const change = direction => {
          if (!Number.isFinite(quantity.valueAsNumber)) quantity.value = quantity.min || '1';
          direction < 0 ? quantity.stepDown() : quantity.stepUp();
          quantity.dispatchEvent(new Event('input', {bubbles: true}));
          quantity.dispatchEvent(new Event('change', {bubbles: true}));
          sync();
        };
        on(minus, 'click', () => change(-1));
        on(plus, 'click', () => change(1));
        on(quantity, 'input', sync);
        on(quantity, 'change', sync);
        sync();
      }
    }

    const grid = root.querySelector('[data-font-grid]');
    const toggle = root.querySelector('[data-premium-fonts]');
    let fontsReady = false;
    function initFonts() {
      if (fontsReady || !grid?.children.length || !toggle) return;
      fontsReady = true;
      // v7.17: the configurator owns tab visibility, the row count and the
      // toggle label; this only collapses rows beyond the first six.
      const selected = grid.querySelector('[aria-pressed="true"]');
      const collapsed = !selected || !selected.classList.contains('is-extra');
      grid.classList.toggle('premium-fonts-collapsed', collapsed);
      toggle.setAttribute('aria-expanded', String(!collapsed));
      toggle.textContent = collapsed ? toggle.dataset.more : toggle.dataset.less;
      on(toggle, 'click', () => {
        const wasExpanded = toggle.getAttribute('aria-expanded') === 'true';
        grid.classList.toggle('premium-fonts-collapsed', wasExpanded);
        toggle.setAttribute('aria-expanded', String(!wasExpanded));
        toggle.textContent = wasExpanded ? toggle.dataset.more : toggle.dataset.less;
        if (wasExpanded) toggle.focus({preventScroll: true});
      });
    }
    function labelZoom(button) {
      button.dataset.premiumLabel = root.dataset.premiumZoom;
      // The existing toggle keeps its own accessible name and pressed state;
      // the brief's wording is shown through CSS generated content only.
    }
    root.querySelectorAll('.name-zoom-toggle').forEach(labelZoom);
    initFonts();
    const observer = new MutationObserver(records => {
      for (const record of records) {
        if (record.target === grid) initFonts();
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches('.name-zoom-toggle')) labelZoom(node);
          node.querySelectorAll('.name-zoom-toggle').forEach(labelZoom);
        }
      }
    });
    observer.observe(root, {childList: true, subtree: true});
    cleanups.push(() => observer.disconnect());

    const closeup = root.querySelector('[data-name-closeup]');
    if (closeup) {
      const syncCloseup = () => {
        const colour = root.querySelector('input[data-color]:checked')?.dataset.color || 'gold';
        const src = closeup.dataset[`closeup${colour[0].toUpperCase()}${colour.slice(1)}`];
        if (src && closeup.getAttribute('src') !== src) closeup.src = src;
      };
      on(document, 'nb:config-change', syncCloseup);
      on(root, 'change', syncCloseup);
      syncCloseup();
    }

    const photos = root.querySelector('.merged-product-photos');
    if (photos) {
      const figures = [...photos.querySelectorAll('.product-photo-grid > .product-photo')];
      if (figures.length) {
        const thumbnails = document.createElement('div');
        thumbnails.className = 'premium-thumbnails';
        figures.forEach((figure, index) => {
          const image = figure.querySelector('img');
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-label', image.alt);
          button.setAttribute('aria-pressed', String(index === 0));
          const thumb = document.createElement('img');
          thumb.src = image.src;
          thumb.alt = '';
          thumb.width = thumb.height = 64;
          button.append(thumb);
          thumbnails.append(button);
          figure.classList.toggle('is-active', index === 0);
          on(button, 'click', () => {
            figures.forEach((item, i) => item.classList.toggle('is-active', i === index));
            [...thumbnails.children].forEach((item, i) => item.setAttribute('aria-pressed', String(i === index)));
          });
          // Two-name photos are still switched by the original configurator.
          const imageObserver = new MutationObserver(() => { thumb.src = image.src; });
          imageObserver.observe(image, {attributes: true, attributeFilter: ['src']});
          cleanups.push(() => imageObserver.disconnect());
        });
        photos.append(thumbnails);
        photos.classList.add('premium-gallery-ready');
        cleanups.push(() => { thumbnails.remove(); photos.classList.remove('premium-gallery-ready'); });
      }
    }
    // PC: the choose panel is taller than the screen, so stick it by its bottom edge
    // (top = screen - panel). The preview and zoom stay in normal flow while visible.
    const panel = root.querySelector('.merged-config__form');
    if (panel && 'ResizeObserver' in window) {
      const wide = window.matchMedia('(min-width: 761px)');
      const place = () => {
        panel.style.top = wide.matches ? `${Math.min(24, window.innerHeight - panel.offsetHeight - 24)}px` : '';
      };
      const panelObserver = new ResizeObserver(place);
      panelObserver.observe(panel);
      on(window, 'resize', place);
      place();
      cleanups.push(() => { panelObserver.disconnect(); panel.style.top = ''; });
    }
    states.set(root, () => { cleanups.forEach(cleanup => cleanup()); states.delete(root); });
  }
  const initAll = scope => scope.querySelectorAll('[data-section-type="configurator"]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
  else initAll(document);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-section-type="configurator"]').forEach(root => states.get(root)?.());
  });
})();
